import { getCsrfToken } from "../auth/session-api.js";

/**
 * The Player AI-owned mood command API (plan §14): writes go straight to
 * the owner service through the auth BFF proxy (`/api/player-ai`), never
 * through the dashboard layer — providers are read-only (§10.7). After a
 * write, the host invalidates the `personal-state/mood-today@1` slot query.
 */

export interface MoodCheckWrite {
  readonly score: number;
  readonly note: string | null;
}

const baseUrl = (): string =>
  (import.meta.env["VITE_PLAYER_AI_URL"] as string | undefined) ??
  "/api/player-ai";

export async function putMoodToday(input: MoodCheckWrite): Promise<void> {
  const csrf = getCsrfToken();
  const response = await fetch(`${baseUrl()}/v2/mood-check/today`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(csrf ? { "x-csrf-token": csrf } : {}),
    },
    body: JSON.stringify({ score: input.score, note: input.note }),
  });
  if (!response.ok) {
    throw new Error(`Mood check-in failed (${response.status}).`);
  }
}

/**
 * Hide the Mood Check widget for today, or bring it back.
 *
 * The body carries no date: Player AI derives the actor's current local day
 * from their stored time zone, the same rule a check-in uses. A separate
 * command from `putMoodToday` on purpose — visibility is not a mood entry, and
 * one request must never be able to write both.
 */
export async function putMoodVisibility(hidden: boolean): Promise<void> {
  const csrf = getCsrfToken();
  const response = await fetch(`${baseUrl()}/v2/mood-check/visibility`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(csrf ? { "x-csrf-token": csrf } : {}),
    },
    body: JSON.stringify({ hidden }),
  });
  if (!response.ok) {
    throw new Error(`Mood visibility change failed (${response.status}).`);
  }
}
